/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 07/02/22
 */

import { FormContainer, LoadingSpinner } from '@/components';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { useWorkplan } from '@/components/contexts/WorkplanContext/useWorkplan';
import { FormNotLoadedAlert } from '@/components/FormAlert/FormNotLoadedAlert';
import { FormNotSubmittedAlert } from '@/components/FormAlert/FormNotSubmittedAlert';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import BaseJsonForm, { HandleChangeFunc, HandleTouchedChangeFunc } from '@/components/WorkplanItemForm/BaseJsonForm';
import { TouchedState } from '@/components/WorkplanItemForm/TouchedStateProvider';
import { WorkplanNotLoadedAlert } from '@/components/WorkplanNotLoadedAlert/WorkplanNotLoadedAlert';
import InlineRefParser from '@/forms-renderers/inlineRefParser/inlineRefParser';
import stripHiddenData from '@/forms-renderers/stripHiddenData/stripHiddenData';
import { BaseFormRequest } from '@/wpt-lib/interfaces/baseFormRequest';
import { BaseFormResponse } from '@/wpt-lib/interfaces/baseFormResponse';
import { FormChange } from '@/wpt-lib/interfaces/EntityChange';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import { useDebounce } from '@/utils/hooks/useDebounce';
import usePageTitle from '@/utils/hooks/usePageTitle';
import { usePrevious } from '@/utils/hooks/usePrevious';
import { isNullish } from '@/wpt-lib/utils/isNullish';
import { joinPaths } from '@/utils/joinPaths';
import { useDeleteRequest, useGetRequest, usePatchRequest, usePostRequest } from '@/utils/useStatefulRequests';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { JsonFormsCore, JsonSchema, UISchemaElement } from '@jsonforms/core';
import { AxiosResponse } from 'axios';
import { isEqual } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ExitFormAlert } from '../Table/ExitFormAlert';
import { parseFormData } from '@/wpt-lib/utils/formData';
import useWorkplanVersion from '@/components/contexts/VersionContext/useWorkplanVersion';
import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';

interface WorkplanItemFormMetadata {
    fte: number;
    probationDetails: string;
    researchAllocation: number;
    serviceAllocation: number;
    teachingAllocation: number;
    gradeName: string;
}

export interface WorkplanItemFormConfig extends FormChange {
    hideRequiredAsterisk?: boolean;
    isAuthor: boolean;
    metadata: WorkplanItemFormMetadata;
    readonly: boolean;
    isSubmitted: boolean;
    mode?: 'create' | 'edit' | 'view';
}

export interface WorkplanItemFormProps<T> {
    name: string;
    baseEndpoint: string;
    schema: JsonSchema;
    uischema: UISchemaElement;
    readonly: boolean;
    page?: boolean;
    autosave?: number;
    pageTitle: (mode: 'Add' | 'Edit' | 'View' | 'Exit' | unknown) => string;

    convertFormDataToRequestBody(completed: boolean, formData: Record<string, unknown>, formDataString: string): T;
}

const WorkplanItemForm = <RequestBody extends BaseFormRequest, ResponseBody extends BaseFormResponse>(
    props: WorkplanItemFormProps<RequestBody>,
) => {
    const { name, convertFormDataToRequestBody, baseEndpoint, schema, uischema, pageTitle, readonly, page } = props;

    const { formId } = useParams<{ formId: string }>();

    const { makePath, supervisor, mode, navigatePath } = useNavigateOptions();

    const [exitAlert, setExitAlert] = useState(false);
    const { workplan, loading: workplanLoading, error: workplanError } = useWorkplan();
    const { workplanVersion } = useWorkplanVersion();

    const { currentAssignment } = useAssignment();
    const { makeRequest: makeDeleteRequest, loading: isDeleting, error: deleteError } = useDeleteRequest<Response>();
    const {
        makeRequest: makeGetRequest,
        loading: isGettingForm,
        responseData: loadedForm,
        error: formLoadingError,
    } = useGetRequest<ResponseBody>();
    const {
        makeRequest: makePostRequest,
        loading: isCreatingForm,
        error: updatingError,
    } = usePostRequest<ResponseBody, RequestBody>();
    const {
        makeRequest: makePatchRequest,
        loading: isUpdatingForm,
        error: savingError,
    } = usePatchRequest<ResponseBody, RequestBody>();

    const setTitleByAction = () => {
        switch (mode) {
            case 'create':
                return pageTitle('Add');
            case 'edit':
                return pageTitle('Edit');
            default:
                return pageTitle('View');
        }
    };

    usePageTitle(setTitleByAction());

    const [lastSavedForm, setLastSavedForm] = useState<ResponseBody | undefined>();

    const [initialJsonformsData, setInitialJsonFormsData] = useState<Record<string, unknown> | null>(
        formId ? null : {},
    );

    const [currentJsonFormsData, setCurrentJsonFormsData] = useState<Record<string, unknown> | null>(
        initialJsonformsData,
    );

    const previousJsonformsData = usePrevious(currentJsonFormsData, currentJsonFormsData);
    const [jsonformsErrors, setJsonFormsErrors] = useState<JsonFormsCore['errors']>();
    const [, setJsonFormsTouched] = useState<TouchedState>();

    const compiledUISchema = useMemo(() => InlineRefParser.expandRefs(uischema), [uischema]);

    const requestBody = (isAutosave: boolean, jsonFormData: Record<string, unknown>) => {
        const strippedData = stripHiddenData(jsonFormData, compiledUISchema);
        return convertFormDataToRequestBody(!isAutosave, strippedData, JSON.stringify(strippedData));
    };

    const [openErrorMessage, setOpenErrorMessage] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const saveForm = async (isAutosave: boolean) => {
        setIsSubmitted(true);

        if (!currentJsonFormsData || !jsonformsErrors || !!jsonformsErrors.length) {
            setOpenErrorMessage(true);
            return;
        }
        const newData = requestBody(isAutosave, currentJsonFormsData);
        try {
            let res: AxiosResponse<ResponseBody>;
            if (formId || lastSavedForm) {
                res = await makePatchRequest(joinPaths(baseEndpoint, `${lastSavedForm?.id ?? formId}`), newData, {
                    params: { assignment: currentAssignment?.id },
                });
            } else {
                res = await makePostRequest(baseEndpoint, newData, {
                    params: { workplan: workplan?.id, assignment: currentAssignment?.id },
                });
            }
            setLastSavedForm(res.data);
            if (!isAutosave) {
                navigateRoute();
            }
        } catch (e) {
            // handled by hook
        }
    };

    const cancelAutosave = useDebounce(
        () => {
            if (!isEqual(previousJsonformsData, currentJsonFormsData) && !isNullish(props.autosave)) {
                if (readonly || supervisor || !isEditable || !jsonformsErrors || !!jsonformsErrors.length) {
                    return;
                } else {
                    saveForm(true);
                }
            }
        },
        props.autosave ?? 0,
        [currentJsonFormsData],
    );

    const thisWorkplan = loadedForm ? loadedForm?.workplanItem?.workplanVersion.workplan : workplan;

    const getBreadCrumbs = () => {
        return [
            {
                label: `My ${thisWorkplan?.year} Workplan`,
                path: makePath(thisWorkplan?.id ?? '', workplanVersion?.version ?? ''),
                isCurrent: false,
            },
            {
                label: setTitleByAction(),
                path: '#',
                isCurrent: true,
            },
        ];
    };

    const onChange = useCallback<HandleChangeFunc>(({ data, errors }) => {
        if (!errors?.length) {
            setOpenErrorMessage(false);
        }
        setCurrentJsonFormsData((oldData) => (isEqual(data, oldData) ? oldData : data));
        setJsonFormsErrors((oldErrors) => (isEqual(errors, oldErrors) ? oldErrors : errors));
    }, []);
    const onTouchedChange = useCallback<HandleTouchedChangeFunc>(({ touched }) => {
        setJsonFormsTouched((oldTouched) => (isEqual(touched, oldTouched) ? oldTouched : touched));
    }, []);

    useAsyncLayoutEffect(async () => {
        if (formId) {
            try {
                const res = await makeGetRequest(joinPaths(baseEndpoint, `${formId}`), {
                    params: { assignment: currentAssignment?.id },
                });
                const formData = parseFormData(res.data.workplanItem.formData);
                if (
                    workplan?.year &&
                    workplan.year >= 2024 &&
                    formData.goalType &&
                    typeof formData.goalType === 'string'
                )
                    delete formData.goalType;

                setInitialJsonFormsData(formData);
            } catch (e) {
                // handled by hook
            }
        }
    }, [currentAssignment]);

    const isEditable = useMemo(() => !readonly && !loadedForm?.workplanItem.approved, [loadedForm, readonly]);

    const formMetadata = useMemo((): WorkplanItemFormMetadata => {
        const thisWorkplan = loadedForm ? loadedForm?.workplanItem?.workplanVersion.workplan : workplan;

        const assignment = thisWorkplan?.assignment ?? currentAssignment;

        const isProbationEnded = new Date(`${assignment?.probationEndDate}`) < new Date();
        const probationDetails =
            !isProbationEnded && assignment?.probationEndDate
                ? `Probation ends on: ${new Date(assignment.probationEndDate).toLocaleDateString()}`
                : '';

        return {
            probationDetails,
            teachingAllocation: workplanVersion?.teachingWorkloadPortion ?? 0,
            researchAllocation: workplanVersion?.researchWorkloadPortion ?? 0,
            serviceAllocation: workplanVersion?.serviceWorkloadPortion ?? 0,
            fte: workplanVersion?.fte ?? 1,
            // same as expectation-level const in schema "A", "B", "C", "D", "E"
            gradeName: workplanVersion?.gradeName?.split(' ')?.[1] ?? '',
        };
    }, [loadedForm, workplan, currentAssignment]);

    const config = useMemo(
        (): WorkplanItemFormConfig => ({
            isSubmitted: isSubmitted,
            metadata: formMetadata,
            readonly: readonly || !isEditable || supervisor,
            isAuthor:
                (formId &&
                    loadedForm?.workplanItem.workplanVersion.workplan.assignment?.id === currentAssignment?.id) ||
                !formId,
            formChanges: loadedForm?.formChanges ?? {},
            changed: loadedForm?.changed ?? false,
            mode,
        }),
        [formMetadata, isEditable, supervisor, formId, loadedForm, currentAssignment, isSubmitted, mode],
    );

    if (isGettingForm || workplanLoading) {
        return <LoadingSpinner fullScreen />;
    }

    if (workplanError) {
        return <WorkplanNotLoadedAlert />;
    }

    if (formLoadingError) {
        return <FormNotLoadedAlert formTitle={name} actionName={`${formId ? 'update' : 'create'} ${name}`} />;
    }

    const deleteAutosave = async () => {
        cancelAutosave();
        try {
            if (lastSavedForm) {
                if (isNullish(formId)) {
                    // Delete form
                    await makeDeleteRequest(`${baseEndpoint}/${lastSavedForm.id}`, {
                        params: { assignment: currentAssignment?.id },
                    });
                } else {
                    // Reset to previous version
                    if (initialJsonformsData) {
                        const initData = requestBody(true, initialJsonformsData);
                        await makePatchRequest(joinPaths(baseEndpoint, `${lastSavedForm.id}`), initData, {
                            params: { assignment: currentAssignment?.id },
                        });
                    }
                }
            }
            navigateRoute();
        } catch (e) {
            //handled by hook
        }
    };

    const navigateRoute = () => {
        if (workplan && workplanVersion) navigatePath(workplan.id, workplanVersion.version);
    };

    const displayFormErrorMessage = () => {
        const errorMessage = 'There are errors in this form, please resolve them before you submit.';
        return (
            <Box justifySelf="flex-end">
                <SimpleAlert variant="subtle">{errorMessage}</SimpleAlert>
            </Box>
        );
    };

    return (
        <FormContainer breadCrumbItems={getBreadCrumbs()} workplan={workplan} page={!!page}>
            {initialJsonformsData && (
                <BaseJsonForm
                    schema={schema}
                    uischema={uischema}
                    onChange={onChange}
                    onTouchedChange={onTouchedChange}
                    initialData={initialJsonformsData}
                    config={config}
                />
            )}
            {isEditable && (
                <VStack justifyContent={'flex-end'} alignItems="flex-end">
                    {openErrorMessage && displayFormErrorMessage()}
                    <HStack justifyContent={'space-between'} py={3} spacing={3} width={'100%'} alignItems={'center'}>
                        <Button
                            colorScheme="blue"
                            isLoading={isCreatingForm || isUpdatingForm}
                            onClick={() => {
                                cancelAutosave();
                                saveForm(false);
                            }}
                        >
                            Save & Return to Dashboard
                        </Button>
                        {lastSavedForm && (
                            <Text>
                                Autosaved{': '}
                                {new Date(lastSavedForm.workplanItem.updatedAt).toLocaleTimeString()}
                                {', '}
                                {new Date(lastSavedForm.workplanItem.updatedAt).toDateString()}
                            </Text>
                        )}

                        {(mode === 'create' || mode === 'edit') && (
                            <Button
                                colorScheme="gray"
                                onClick={() => {
                                    if (currentJsonFormsData === initialJsonformsData) {
                                        deleteAutosave();
                                    } else {
                                        setExitAlert(true);
                                    }
                                }}
                            >
                                Discard
                            </Button>
                        )}
                    </HStack>
                </VStack>
            )}
            {(!!savingError || !!updatingError) && <FormNotSubmittedAlert formTitle={props.name} />}
            <ExitFormAlert
                deleteAction={deleteAutosave}
                header={`Discard ${setTitleByAction()}`}
                deleteError={deleteError || savingError}
                action={mode === 'create' ? `adding ${name}` : `editing ${name}`}
                workplanType={name}
                isOpen={exitAlert}
                isDeleting={isDeleting || isUpdatingForm}
                onClose={() => setExitAlert(false)}
            />
        </FormContainer>
    );
};

export default WorkplanItemForm;
